import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import RegistrationFormModal from "app/components/RegistrationFormModal/RegistrationFormModal";
import { LanguageSelectionHelper } from "app/helpers/languageSelectionHelper";
import NavBar from "app/partials/NavBar/NavBar";
import { PromoPage, ResponsiveScreenSizesInt } from "app/utils/const";
import { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import ENV from "../../../../../config";
import Footer from "../Footer/Footer";
import MobilePromoItem from "./components/MobilePromoItem";
import PromoImageContainer from "./components/PromoImage";
import PromoItem from "./components/PromoItem";
import PromoPageSEOBlurb from "./components/PromoPageSEOBlurb";
import "./PromotionalPage.scss";
//
interface PromoContent {
  title: string;
  subTitle1: string;
  description: string;
  buttonUrl: string;
  urlPromoCode?: string;
  image1: { url: string; alt: string };
  mobileImageOne: { url: string; alt: string };
  image1Title: string;
  image1Text: string;
  image1Button: string;
  additionalPromoItems: { title: string; text: string }[];
  claimOfferText: string;
  claimOfferButton: string;
  claimOfferButtonUrl: string;
  image2: { url: string; alt: string };
  mobileImageTwo: { url: string; alt: string };
  image2Title: string;
  image2Text: string;
  image2Button: string;
  termsAndConditions: string;
  howItWorksDescription: string;
  subTitle2: string;
}

const getPromoUrl = (url: string, urlPromoCode?: string) => {
  if (!urlPromoCode) return url;
  const urlObj = new URL(url, window.location.origin);
  urlObj.searchParams.set("promo", urlPromoCode);
  return urlObj.toString();
};

const Promo = () => {
  const [content, setContent] = useState<PromoContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [locale, setLocale] = useState("en-US");

  const spaceId = PromoPage.CONTENTFUL_SPACE_ID;
  const accessToken = PromoPage.CONTENTFUL_ACCESS_TOKEN;

  // Always use master environment now
  const environment = PromoPage.CONTENTFUL_ENVIRONMENT_MASTER;

  // Entry IDs for different environments
  const prodEntryId = "vxtryAu8bbtJwK3mpVp7b"; // prod entry ID
  const stagingEntryId = "79BSkpr5xHBvjqSty1HJhL"; // staging/dev entry ID

  // Logic to choose entry based on environment
  // keeping the check below in for QA so that they can test
  // that 3et PP also shows the master entry
  const isPreProd = ENV.DOMAIN === "www.3etpp.com";

  const entryId =
    // If it's production OR preprod, use prodEntryId
    ENV.PRODUCTION === "TRUE" || isPreProd
      ? prodEntryId
      : // If NOT production and NOT preprod, use stagingEntryId
      ENV.PRODUCTION === "FALSE" && !isPreProd
      ? stagingEntryId
      : stagingEntryId;

  const baseUrl = `https://cdn.contentful.com/spaces/${spaceId}/environments/${environment}/entries`;

  const createEntryUrl = (entryId: string) =>
    `${baseUrl}/${entryId}?access_token=${accessToken}&locale=${locale}`;

  const createAssetUrl = (assetId: string) =>
    `https://cdn.contentful.com/spaces/${spaceId}/assets/${assetId}?access_token=${accessToken}`;

  useEffect(() => {
    if (LanguageSelectionHelper.checkSelectedLanguage().includes("es")) {
      setLocale("es");
    }
  }, []);

  useEffect(() => {
    async function fetchData() {
      try {
        const response = await fetch(createEntryUrl(entryId));
        const data = await response.json();

        const fetchImage = async (imageField: any) => {
          if (!imageField?.sys?.id) return null;
          const imgResponse = await fetch(createAssetUrl(imageField.sys.id));
          const imgData = await imgResponse.json();
          return {
            url: `https:${imgData.fields.file.url}`,
            alt: imgData.fields.title || "Promo Image",
          };
        };

        const image1 = await fetchImage(data.fields.image1);
        const mobileImageOne =
          (await fetchImage(data.fields.mobileImageOne)) || image1;
        const image2 = await fetchImage(data.fields.image2);
        const mobileImageTwo =
          (await fetchImage(data.fields.mobileImageTwo)) || image2;

        setContent({
          ...data.fields,
          image1: image1 || { url: "", alt: "Promo Image" },
          mobileImageOne: mobileImageOne || { url: "", alt: "Promo Image" },
          image2: image2 || { url: "", alt: "Claim Image" },
          mobileImageTwo: mobileImageTwo || { url: "", alt: "Claim Image" },
        });

        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    }

    fetchData();
  }, [locale, entryId]);

  const [isMobile, setIsMobile] = useState(
    window.innerWidth <= ResponsiveScreenSizesInt.mobile
  );

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= ResponsiveScreenSizesInt.mobile);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  // any translations will be in english and Spanish,
  // pt/zh will just be english for now as contentful only allows for two translations
  if (loading)
    return (
      <p className="loading-text">
        <FormattedMessage tagName="span" id="competition-expanded-loading" />
      </p>
    );
  if (!content)
    return (
      <p className="error-text">
        <FormattedMessage tagName="span" id="content-not-found" />
      </p>
    );

  const {
    title,
    subTitle1,
    description,
    buttonUrl,
    urlPromoCode,
    image1,
    mobileImageOne,
    image1Title,
    image1Text,
    image1Button,
    additionalPromoItems,
    claimOfferText,
    claimOfferButton,
    claimOfferButtonUrl,
    image2,
    mobileImageTwo,
    image2Title,
    image2Text,
    image2Button,
    subTitle2,
    termsAndConditions,
    howItWorksDescription,
  } = content;

  return (
    <>
      <NavBar setLocale={setLocale} />
      <RegistrationFormModal />
      <div className="promo-container">
        <br />
        {/* First Promo Image */}
        <PromoImageContainer
          image={image1}
          mobileImage={mobileImageOne}
          title={image1Title}
          text={image1Text}
          button={image1Button}
          buttonUrl={buttonUrl}
          urlPromoCode={urlPromoCode}
          isMobile={isMobile}
        />

        {/* Main Content */}
        <div className="promotional-page-content">
          <div className="promotional-page-gradient-circle left"></div>
          <h1 className="promotional-page-title">{title || "Default Title"}</h1>
          {howItWorksDescription && (
            <div className="promotional-page-subtitle-decoration">
              <h2>
                <div className="promotional-page-subTitle2-text">
                  {subTitle1}
                </div>
              </h2>
              {typeof howItWorksDescription === "string" ? (
                <p>{howItWorksDescription}</p>
              ) : (
                documentToReactComponents(howItWorksDescription)
              )}
            </div>
          )}

          {/* Promo items, displayed differently based on screen size */}
          <div className="promotional-page-promo-items">
            {additionalPromoItems.map((item, index) =>
              isMobile ||
              (window.innerWidth > 750 && window.innerWidth > 755) ? (
                <PromoItem
                  key={index}
                  index={index}
                  title={item.title}
                  text={item.text}
                />
              ) : (
                <MobilePromoItem
                  key={index}
                  index={index}
                  title={item.title}
                  text={item.text}
                />
              )
            )}
          </div>

          {/* Claim Offer Section */}
          <div className="promotional-page-claim-offer-container">
            <a
              href={getPromoUrl(claimOfferButtonUrl, urlPromoCode)}
              className="promotional-page-claim-offer-button"
              rel="noopener noreferrer"
            >
              {claimOfferButton}
            </a>
          </div>
          <div className="promotional-page-description">{claimOfferText}</div>
          <div className="promotional-page-gradient-circle right"></div>

          {/* Second Promo Image */}
          <PromoImageContainer
            image={image2}
            mobileImage={mobileImageTwo}
            title={image2Title}
            text={image2Text}
            button={image2Button}
            buttonUrl={buttonUrl}
            urlPromoCode={urlPromoCode}
            isMobile={isMobile}
          />
        </div>

        {/* Terms and Conditions */}
        {termsAndConditions && (
          <div className="promotional-page-subtitle-decoration">
            <h2>
              <div className="promotional-page-subTitle2-text">{subTitle2}</div>
            </h2>
            {typeof termsAndConditions === "string" ? (
              <p>{termsAndConditions}</p>
            ) : (
              documentToReactComponents(termsAndConditions)
            )}
          </div>
        )}
      </div>
      <div className="promotional-page-footerTop">
        <PromoPageSEOBlurb></PromoPageSEOBlurb>
      </div>
      <Footer />
    </>
  );
};

export default Promo;
